import {
  Alert,
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material'
import CardSectionHeader from 'src/v3/components/card-section-header'
import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd'
import { StreamStep } from 'src/v3/core/types'
import {
  AddIcon,
  EditIcon,
  PreviewIcon,
  QuizIcon,
  RemoveIcon,
  VideoIcon,
  ShowNotesIcon,
} from 'src/v3/core/icons'
import { Status } from 'src/store/store'

import { updateCourseSteps, removeContentFromCourses } from '../../utils/api'
import {
  getStepIcon,
  getStepIconColor,
  getStepLabel,
} from '../utils/step-type-helpers'
import StepCard from './step-card'
import ConfirmDialog from './modals/confirm-dialog'
import {
  VideoLibraryItem,
  ShowNotesLibraryItem,
  QuizLibraryItem,
  CreateContentTypes,
} from '../types'

type PendingRemoval = {
  id: string
  contentType: 'video' | 'showNotes' | 'quiz'
  name: string
}

type PublishGuard = {
  triggerGuard: (
    wouldViolate: boolean,
    message: string,
    action: () => Promise<void> | void,
  ) => void
}

type CourseStepsSectionProps = {
  courseID: string
  courseName?: string
  steps: StreamStep[]
  stepsStatus: Status
  videos: VideoLibraryItem[]
  showNotes: ShowNotesLibraryItem[]
  quizzes: QuizLibraryItem[]
  onSaveSuccess?: () => void
  onError?: (message: string) => void
  onSavingStateChange?: (isSaving: boolean) => void
  onAddVideo?: () => void
  onAddShowNotes?: () => void
  onAddQuiz?: () => void
  hasQuiz?: boolean
  onEditVideo?: (videoID: string) => void
  onEditShowNotes?: (showNotesID: string) => void
  onEditQuiz?: (quizID: string) => void
  onPreviewVideo?: (videoID: string) => void
  onPreviewShowNotes?: (showNotesID: string) => void
  onPreviewQuiz?: (quizID: string) => void
  onRemoveContentSuccess: (contentType: CreateContentTypes) => void
  locked?: boolean
  onUnpublish?: () => void
  unpublishDisabled?: boolean
  isPublished?: boolean
  publishGuard?: PublishGuard
}

export default function CourseStepsSection({
  courseID,
  courseName,
  steps: stepsFromRedux,
  stepsStatus,
  videos,
  showNotes,
  quizzes,
  onSaveSuccess,
  onError,
  onSavingStateChange,
  onAddVideo,
  onAddShowNotes,
  onAddQuiz,
  hasQuiz,
  onEditVideo,
  onEditShowNotes,
  onEditQuiz,
  onPreviewVideo,
  onPreviewShowNotes,
  onPreviewQuiz,
  onRemoveContentSuccess,
  locked,
  onUnpublish,
  unpublishDisabled,
  isPublished,
  publishGuard,
}: CourseStepsSectionProps): ReactElement {
  let contentNameMap = useMemo(() => {
    let map = new Map<string, string>()
    videos.forEach((v) => map.set(v.id, v.name))
    showNotes.forEach((sn) => map.set(sn.id, sn.name))
    quizzes.forEach((q) => map.set(q.id, q.name || 'Quiz'))
    return map
  }, [videos, showNotes, quizzes])

  let [localSteps, setLocalSteps] = useState<StreamStep[]>([])
  let [isSaving, setIsSaving] = useState(false)
  let [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(
    null,
  )

  let isLoading = isSaving || stepsStatus === 'pending'
  let isInteractionDisabled = isLoading || !!locked

  useEffect(() => {
    setLocalSteps(stepsFromRedux)
  }, [stepsFromRedux])

  useEffect(() => {
    if (onSavingStateChange) {
      onSavingStateChange(isSaving)
    }
  }, [isSaving, onSavingStateChange])

  let stepsToDisplay = localSteps

  let nonQuizSteps = useMemo(
    () => stepsToDisplay.filter((s) => !s.quizID),
    [stepsToDisplay],
  )
  let quizStep = useMemo(
    () => stepsToDisplay.find((s) => !!s.quizID) ?? null,
    [stepsToDisplay],
  )

  let saveSteps = useCallback(
    (steps: StreamStep[]) => {
      setIsSaving(true)

      let stepsToSave = steps.map((step) => ({
        videoID: step.videoID,
        showNotesID: step.showNotesID,
        quizID: step.quizID,
      }))

      updateCourseSteps(courseID, stepsToSave)
        .then(() => {
          if (onSaveSuccess) {
            onSaveSuccess()
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to save step order:', error)
          // Revert to Redux state on error
          setLocalSteps(stepsFromRedux)
          if (onError) {
            onError(
              error instanceof Error
                ? error.message
                : 'Failed to save step order',
            )
          }
        })
        .finally(() => {
          setIsSaving(false)
        })
    },
    [courseID, stepsFromRedux, onSaveSuccess, onError],
  )

  let handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || isInteractionDisabled) return

      let sourceIndex = result.source.index
      let destinationIndex = result.destination.index

      if (sourceIndex === destinationIndex) return

      let removed = nonQuizSteps[sourceIndex]
      let withoutRemoved = [
        ...nonQuizSteps.slice(0, sourceIndex),
        ...nonQuizSteps.slice(sourceIndex + 1),
      ]

      let reordered = [
        ...withoutRemoved.slice(0, destinationIndex),
        removed,
        ...withoutRemoved.slice(destinationIndex),
      ]

      let allSteps = quizStep ? [...reordered, quizStep] : reordered
      let updatedSteps = allSteps.map((step, index) => ({
        ...step,
        stepIndex: index,
      }))

      setLocalSteps(updatedSteps)
      saveSteps(updatedSteps)
    },
    [nonQuizSteps, quizStep, isInteractionDisabled, saveSteps],
  )

  let handleRemoveStep = useCallback(
    (stepID: string) => {
      if (isInteractionDisabled) return

      let removedStep = stepsToDisplay.find((s) => s.id === stepID)
      let updatedNonQuiz = nonQuizSteps.filter((s) => s.id !== stepID)
      let remainingQuiz = quizStep && quizStep.id !== stepID ? quizStep : null

      let allSteps = remainingQuiz
        ? [...updatedNonQuiz, remainingQuiz]
        : updatedNonQuiz
      let updatedSteps = allSteps.map((step, index) => ({
        ...step,
        stepIndex: index,
      }))

      let wouldViolate =
        !!isPublished &&
        (!!removedStep?.quizID ||
          allSteps.length === 0 ||
          updatedNonQuiz.length === 0)

      if (publishGuard && wouldViolate) {
        let reason =
          allSteps.length === 0
            ? 'Removing this step will leave the course with no steps, violating publish requirements.'
            : removedStep?.quizID
              ? 'Removing the quiz will leave the course without a quiz as the last step, violating publish requirements.'
              : 'Removing this step will leave the course without any video or show-notes content, violating publish requirements.'
        publishGuard.triggerGuard(true, reason, () => {
          setLocalSteps(updatedSteps)
          saveSteps(updatedSteps)
        })
      } else {
        setLocalSteps(updatedSteps)
        saveSteps(updatedSteps)
      }
    },
    [
      stepsToDisplay,
      nonQuizSteps,
      quizStep,
      isInteractionDisabled,
      saveSteps,
      isPublished,
      publishGuard,
    ],
  )

  let handleAddContent = useCallback(
    (contentID: string, contentType: CreateContentTypes) => {
      if (isInteractionDisabled) return
      if (contentType === 'quiz' && quizStep) return

      let newStepID = `temp-${Date.now()}-${Math.random()}`

      let stepType: StreamStep['type'] =
        contentType === 'video'
          ? 'Video'
          : contentType === 'showNotes'
            ? 'ShowNotes'
            : contentType === 'quiz'
              ? 'Quiz'
              : 'Unassigned'

      let newStep: StreamStep = {
        id: newStepID,
        type: stepType,
        stepIndex: 0,
        videoID: contentType === 'video' ? contentID : undefined,
        showNotesID: contentType === 'showNotes' ? contentID : undefined,
        quizID: contentType === 'quiz' ? contentID : undefined,
      }

      let allSteps =
        contentType === 'quiz'
          ? [...nonQuizSteps, newStep]
          : [...nonQuizSteps, newStep, ...(quizStep ? [quizStep] : [])]

      let updatedSteps = allSteps.map((step, index) => ({
        ...step,
        stepIndex: index,
      }))

      setLocalSteps(updatedSteps)
      saveSteps(updatedSteps)
    },
    [nonQuizSteps, quizStep, isInteractionDisabled, saveSteps],
  )

  let handleRemoveFromCourse = useCallback(
    (id: string, contentType: CreateContentTypes) => {
      setIsSaving(true)

      return removeContentFromCourses(contentType, id, [courseID])
        .then(() => {
          onRemoveContentSuccess(contentType)
        })
        .catch((error) => {
          if (onError)
            onError(
              error instanceof Error
                ? error.message
                : `Failed to remove ${contentType} from course`,
            )
        })
        .finally(() => setIsSaving(false))
    },
    [courseID, onError, onRemoveContentSuccess],
  )

  let handleRequestRemove = useCallback(
    (id: string, contentType: CreateContentTypes, name: string) => {
      setPendingRemoval({ id, contentType, name })
    },
    [],
  )

  let handleConfirmRemove = useCallback(() => {
    if (!pendingRemoval) return Promise.resolve()
    let { id, contentType } = pendingRemoval
    return handleRemoveFromCourse(id, contentType).finally(() =>
      setPendingRemoval(null),
    )
  }, [pendingRemoval, handleRemoveFromCourse])

  // Compute unused content (content not in any step)
  let unusedContent = useMemo(() => {
    let usedVideoIDs = new Set(
      stepsToDisplay.filter((s) => s.videoID).map((s) => s.videoID),
    )
    let usedShowNotesIDs = new Set(
      stepsToDisplay.filter((s) => s.showNotesID).map((s) => s.showNotesID),
    )
    let usedQuizIDs = new Set(
      stepsToDisplay.filter((s) => s.quizID).map((s) => s.quizID),
    )

    let unusedVideos = videos
      .filter((v) => !usedVideoIDs.has(v.id) && !v.isMigratable)
      .map((v) => ({ ...v, contentType: 'video' as const }))
    let unusedShowNotes = showNotes
      .filter((sn) => !usedShowNotesIDs.has(sn.id))
      .map((sn) => ({ ...sn, contentType: 'showNotes' as const }))
    let unusedQuizzes = quizzes
      .filter((q) => !usedQuizIDs.has(q.id))
      .map((q) => ({ ...q, contentType: 'quiz' as const }))

    return [...unusedVideos, ...unusedShowNotes, ...unusedQuizzes]
  }, [stepsToDisplay, videos, showNotes, quizzes])

  let addContentSection = (
    <Box className="mt-4">
      <CardSectionHeader label="Add Content" component="h4" className="mb-3" />
      <Box className="flex flex-col gap-3 sm:flex-row">
        <Button
          variant="outlined"
          size="large"
          color="success"
          startIcon={<ShowNotesIcon />}
          onClick={onAddShowNotes}
          disabled={!onAddShowNotes || !!locked}
          className="flex-1"
        >
          Add Show Notes
        </Button>
        <Button
          variant="outlined"
          size="large"
          color="primary"
          startIcon={<VideoIcon />}
          onClick={onAddVideo}
          disabled={!onAddVideo || !!locked}
          className="flex-1"
        >
          Add Video
        </Button>
        <Tooltip
          title={
            hasQuiz
              ? "This course already has a quiz. Mid-course checkpoints aren't supported yet."
              : ''
          }
        >
          <span className="flex-1">
            <Button
              variant="outlined"
              size="large"
              color="secondary"
              startIcon={<QuizIcon />}
              onClick={onAddQuiz}
              disabled={!onAddQuiz || !!locked || hasQuiz}
              className="w-full"
            >
              Add Quiz
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )

  if (stepsToDisplay.length === 0 && unusedContent.length === 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No course steps found. Add content below to create your course flow.
        </Typography>
        {addContentSection}
      </Box>
    )
  }

  return (
    <Box>
      {locked && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            onUnpublish && (
              <Button
                color="inherit"
                size="small"
                onClick={onUnpublish}
                disabled={unpublishDisabled}
              >
                Unpublish
              </Button>
            )
          }
        >
          This course is published with legacy content. Unpublish the course
          before editing or rearranging steps.
        </Alert>
      )}
      {stepsToDisplay.length > 0 ? (
        <>
          <Box className="flex items-center justify-between mb-2">
            <Typography
              id="steps-instructions"
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              Use Tab to navigate between steps. Press Space to pick up a step,
              Arrow keys to move it, and Space again to drop it.
            </Typography>
            {isLoading && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary' }}
                role="status"
                aria-live="polite"
              >
                Saving...
              </Typography>
            )}
          </Box>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="course-steps">
              {(provided) => (
                <Box
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="flex flex-col gap-2"
                  aria-describedby="steps-instructions"
                >
                  {nonQuizSteps.map((step, index) => {
                    let contentID = step.videoID || step.showNotesID
                    let contentName = contentID
                      ? contentNameMap.get(contentID) || 'Unknown'
                      : 'Unknown Step'

                    return (
                      <Draggable
                        key={step.id}
                        draggableId={step.id}
                        index={index}
                        isDragDisabled={isInteractionDisabled}
                      >
                        {(draggableProvided, snapshot) => (
                          <Box
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            sx={{
                              opacity: isInteractionDisabled ? 0.5 : 1,
                              transition: 'opacity 0.2s',
                              pointerEvents: isInteractionDisabled
                                ? 'none'
                                : 'auto',
                            }}
                          >
                            <StepCard
                              step={step}
                              contentName={contentName}
                              isDragging={snapshot.isDragging}
                              onRemove={
                                locked
                                  ? undefined
                                  : () => handleRemoveStep(step.id)
                              }
                              onEdit={
                                locked
                                  ? undefined
                                  : step.videoID && onEditVideo
                                    ? () => onEditVideo(step.videoID!)
                                    : step.showNotesID && onEditShowNotes
                                      ? () => onEditShowNotes(step.showNotesID!)
                                      : undefined
                              }
                              onPreview={
                                step.videoID && onPreviewVideo
                                  ? () => onPreviewVideo(step.videoID!)
                                  : step.showNotesID && onPreviewShowNotes
                                    ? () =>
                                        onPreviewShowNotes(step.showNotesID!)
                                    : undefined
                              }
                            />
                          </Box>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>

          {quizStep && (
            <Box
              className="mt-2"
              sx={{
                opacity: isInteractionDisabled ? 0.5 : 1,
                transition: 'opacity 0.2s',
                pointerEvents: isInteractionDisabled ? 'none' : 'auto',
              }}
            >
              <StepCard
                step={quizStep}
                contentName={contentNameMap.get(quizStep.quizID!) || 'Quiz'}
                isDragging={false}
                hideDragHandle
                onRemove={
                  locked ? undefined : () => handleRemoveStep(quizStep.id)
                }
                onEdit={
                  locked
                    ? undefined
                    : quizStep.quizID && onEditQuiz
                      ? () => onEditQuiz(quizStep.quizID!)
                      : undefined
                }
                onPreview={
                  quizStep.quizID && onPreviewQuiz
                    ? () => onPreviewQuiz(quizStep.quizID!)
                    : undefined
                }
              />
            </Box>
          )}
        </>
      ) : (
        <Box className="mb-4">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No active course steps. Add content from the Unused Content section
            below to create your course flow.
          </Typography>
        </Box>
      )}

      <ConfirmDialog
        isOpen={!!pendingRemoval}
        title="Remove from Course"
        message={`Are you sure you want to remove ${pendingRemoval?.contentType === 'video' ? 'Video' : pendingRemoval?.contentType === 'showNotes' ? 'Show Notes' : 'Quiz'}: ${pendingRemoval?.name} from ${courseName ?? 'this course'}?`}
        confirmLabel="Remove"
        onConfirm={handleConfirmRemove}
        onClose={() => setPendingRemoval(null)}
      />

      {unusedContent.length > 0 && (
        <Box className="mt-6">
          <CardSectionHeader
            label="Unused Content"
            component="h4"
            className="mb-3"
          />
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary' }}
            className="block mb-3"
          >
            Content not currently associated with course steps. Add to include
            in the course flow.
          </Typography>

          <Box component="ul" className="flex flex-col gap-2 p-0 m-0 list-none">
            {unusedContent.map((content) => {
              let icon = getStepIcon(content.contentType)
              let iconColor = getStepIconColor(content.contentType)
              let iconLabel = getStepLabel(content.contentType)

              let contentName = content.name

              return (
                <Box
                  component="li"
                  key={content.id}
                  className="flex items-center justify-between p-3 border rounded"
                  sx={{
                    bgcolor: 'background.paper',
                    borderColor: 'divider',
                    opacity: isInteractionDisabled ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                    pointerEvents: isLoading ? 'none' : 'auto',
                  }}
                >
                  <Box className="flex items-center flex-1 min-w-0 gap-3">
                    <Box
                      className="flex"
                      sx={{ color: iconColor }}
                      aria-label={iconLabel}
                      role="img"
                    >
                      {icon}
                    </Box>
                    <Box className="flex-1 min-w-0">
                      <Typography variant="body2" className="truncate">
                        {contentName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="flex gap-1">
                    {content.contentType === 'video' && (
                      <>
                        {onPreviewVideo && (
                          <Tooltip title="Preview video">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isLoading}
                              onClick={() => onPreviewVideo(content.id)}
                              aria-label={`Preview ${contentName}`}
                            >
                              <PreviewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onEditVideo && (
                          <Tooltip title="Edit video">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isInteractionDisabled}
                              onClick={() => onEditVideo(content.id)}
                              aria-label={`Edit ${contentName}`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {content.contentType === 'showNotes' && (
                      <>
                        {onPreviewShowNotes && (
                          <Tooltip title="Preview show notes">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isLoading}
                              onClick={() => onPreviewShowNotes(content.id)}
                              aria-label={`Preview ${contentName}`}
                            >
                              <PreviewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onEditShowNotes && (
                          <Tooltip title="Edit show notes">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isInteractionDisabled}
                              onClick={() => onEditShowNotes(content.id)}
                              aria-label={`Edit ${contentName}`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {content.contentType === 'quiz' && (
                      <>
                        {onPreviewQuiz && (
                          <Tooltip title="Preview quiz">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isLoading}
                              onClick={() => onPreviewQuiz(content.id)}
                              aria-label={`Preview ${contentName}`}
                            >
                              <PreviewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onEditQuiz && (
                          <Tooltip title="Edit quiz">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isInteractionDisabled}
                              onClick={() => onEditQuiz(content.id)}
                              aria-label={`Edit ${contentName}`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    <Tooltip
                      title={isLoading ? 'Saving...' : 'Add to course steps'}
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            handleAddContent(content.id, content.contentType)
                          }
                          disabled={isInteractionDisabled}
                          aria-label={`Add ${contentName} to course steps`}
                        >
                          <AddIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove from course">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={isInteractionDisabled}
                        onClick={() =>
                          handleRequestRemove(
                            content.id,
                            content.contentType,
                            contentName,
                          )
                        }
                        aria-label={`Remove ${contentName} from course`}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      )}

      {addContentSection}
    </Box>
  )
}
